import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { type PayloadAction } from "@reduxjs/toolkit";
import { type AppRouter } from "@server/api/root";
import { type inferRouterOutputs } from "@server/index";
import { trpc } from "@utils/api";
import { close } from "../layout/dialogSlice";
import { createModel, deleteModel, updateModel } from "../crm/crmSlice";

type RouterOutput = inferRouterOutputs<AppRouter>;
type BaseWorkplace =
  RouterOutput["workplaces"]["getWorkplaces"]["records"][number];
type GetNavigationItem =
  RouterOutput["workplaces"]["getWorkplaces"]["records"][number]["navigationItems"][number];

function filterNavigationTreeByIds(
  items: GetNavigationItem[],
  ids: string[],
): GetNavigationItem[] {
  return items
    .filter((item) => !ids.includes(item.id))
    .map((item) =>
      item.children?.length
        ? {
            ...item,
            children: filterNavigationTreeByIds(item.children, ids),
          }
        : item,
    );
}

function upsertNavigationItemInTree(
  items: GetNavigationItem[],
  nextItem: GetNavigationItem,
): GetNavigationItem[] {
  let updated = false;

  const mapped = items.map((item) => {
    if (item.id === nextItem.id) {
      updated = true;
      return nextItem;
    }

    if (
      item.page?.id &&
      nextItem.page?.id &&
      item.page.id === nextItem.page.id
    ) {
      updated = true;
      return nextItem;
    }

    if (item.children?.length) {
      const children = upsertNavigationItemInTree(item.children, nextItem);
      if (children !== item.children) {
        updated = true;
        return { ...item, children };
      }
    }

    return item;
  });

  return updated ? mapped : [...mapped, nextItem];
}

// Use the BaseWorkplace type directly since it already has the correct structure
type Workplace = BaseWorkplace;

export type TeamMember =
  RouterOutput["workplaces"]["getWorkplaces"]["records"][number]["members"][number];

export type ErrorCode =
  | "REQUIRED"
  | "INVALID"
  | "TOO_SHORT"
  | "TOO_LONG"
  | "TAKEN"
  | "RESERVED";

export type FieldErrorCode = "REQUIRED" | "INVALID" | "TAKEN";

export interface WorkplaceState {
  workplace: {
    data: Workplace | null;
    isLoading: boolean;
    isSaving: boolean;
    isValidating: boolean;
    lastValidatedField: string;
    isUploading?: boolean;
    fieldErrors: {
      name?: string;
    };
  };
  workplaces: {
    data: Workplace[];
    isLoading: boolean;
    isFetched: boolean;
  };
  newWorkplace: {
    isLoading?: boolean;
    isSaving?: boolean;
    name: string;
    subdomain: string;
    phone: string;
    errors: {
      name?: string;
      subdomain?: string;
      phone?: string;
    };
  };
  currentMember: {
    data: Workplace["members"][number] | null;
  };
}

const initialState: WorkplaceState = {
  workplace: {
    data: null,
    fieldErrors: {},
    isLoading: true,
    isSaving: false,
    isValidating: false,
    lastValidatedField: "",
  },
  workplaces: {
    data: [],
    isLoading: true,
    isFetched: false,
  },
  newWorkplace: {
    name: "",
    subdomain: "",
    phone: "",
    isLoading: false,
    isSaving: false,
    errors: {},
  },
  currentMember: {
    data: null,
  },
};

export type NewWorkplaceEditableFields = "name" | "subdomain" | "phone";

export const getWorkplaces = createAsyncThunk(
  "getWorkplaces",
  async (arg: { currentUserId: string; subdomain: string }) => {
    if (arg) {
      return await trpc.workplaces.getWorkplaces.query({
        includeSubdomain: arg.subdomain || undefined,
      });
    }
  },
);

export const refreshMyWorkspaceNav = createAsyncThunk(
  "refreshMyWorkspaceNav",
  async () => {
    return await trpc.workplaces.getMyWorkspaceNav.query();
  },
);

export const updateCurrentWorkplace = createAsyncThunk(
  "updateCurrentWorkplace",
  async (payload: Workplace, { dispatch }) => {
    // set current workplace based on the subdomain
    const response = await trpc.workplaces.updateWorkplace.mutate(payload);

    dispatch({
      type: "toast/display",
      payload: {
        code: "WORKPLACE_UPDATED",
        params: {
          name: payload.name,
        },
      },
    });

    return response;
  },
);

export const setWorkplaceArchived = createAsyncThunk(
  "workplace/setArchived",
  async (archived: boolean) => {
    return await trpc.workplaces.setArchived.mutate({ archived });
  },
);

export const createWorkplace = createAsyncThunk(
  "workplace/createWorkplace",
  async (payload: undefined, { dispatch, getState }) => {
    const state = getState() as { workplace: WorkplaceState };

    let clientErrors = false;
    const errors: WorkplaceState["newWorkplace"]["errors"] = {
      phone: undefined,
      name: undefined,
      subdomain: undefined,
    };

    if (state.workplace.newWorkplace.phone.length == 0) {
      errors.phone = "REQUIRED";
      clientErrors = true;
    }

    if (state.workplace.newWorkplace.name.length == 0) {
      errors.name = "REQUIRED";
      clientErrors = true;
    }

    if (state.workplace.newWorkplace.subdomain.length == 0) {
      errors.subdomain = "REQUIRED";
      clientErrors = true;
    }

    if (clientErrors) {
      dispatch(updateNewWorkplaceErrors(errors));
      throw new Error("VALIDATION_FAILED");
    }

    const response = await trpc.workplaces.createWorkplace.mutate(
      state.workplace.newWorkplace,
    );

    if (!response) {
      throw new Error("Failed to create workplace");
    }

    const { data, result, errors: workplaceErrors } = response;

    if (!result && workplaceErrors) {
      dispatch(
        updateNewWorkplaceErrors({
          ...workplaceErrors,
        }),
      );
      throw new Error("Failed to create workplace");
    }

    if (result && data) {
      dispatch(
        updateNewWorkplaceErrors({
          ...state.workplace.newWorkplace.errors,
          subdomain: undefined,
        }),
      );

      dispatch(addWorkplace(data));
      dispatch(close());
      return data;
    }

    throw new Error("Failed to create workplace");
  },
);

export const updatePhone = createAsyncThunk(
  "workplace/updatePhone",
  (
    changes: { id: NewWorkplaceEditableFields; value: string },
    { dispatch, getState },
  ) => {
    const state = getState() as { workplace: WorkplaceState };

    // constants
    const PHONE_REGEX = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

    dispatch(updateNewWorkplaceField(changes));

    if (changes.value.length > 0) {
      if (!changes.value.match(PHONE_REGEX)) {
        return dispatch(
          updateNewWorkplaceErrors({
            ...state.workplace.newWorkplace.errors,
            phone: "INVALID",
          }),
        );
      }
    }

    return dispatch(
      updateNewWorkplaceErrors({
        ...state.workplace.newWorkplace.errors,
        phone: undefined,
      }),
    );
  },
);

export const updateSubdomain = createAsyncThunk(
  "workplace/updateSubdomain",
  async (
    changes: { id: NewWorkplaceEditableFields; value: string },
    { dispatch, getState },
  ) => {
    const state = getState() as { workplace: WorkplaceState };

    // constants
    const SUBDOMAIN_REGEX = /^[a-z0-9_-]+$/;
    const SUBDOMAIN_MIN_LENGTH = 3;
    const SUBDOMAIN_MAX_LENGTH = 19;

    dispatch(updateNewWorkplaceField(changes));

    if (changes.value.length > 0) {
      if (changes.value.length < SUBDOMAIN_MIN_LENGTH) {
        return dispatch(
          updateNewWorkplaceErrors({
            ...state.workplace.newWorkplace.errors,
            subdomain: "TOO_SHORT",
          }),
        );
      }

      if (changes.value.length > SUBDOMAIN_MAX_LENGTH) {
        return dispatch(
          updateNewWorkplaceErrors({
            ...state.workplace.newWorkplace.errors,
            subdomain: "TOO_LONG",
          }),
        );
      }

      if (!changes.value.match(SUBDOMAIN_REGEX)) {
        return dispatch(
          updateNewWorkplaceErrors({
            ...state.workplace.newWorkplace.errors,
            subdomain: "INVALID",
          }),
        );
      }

      const { error, result } = await trpc.workplaces.validateSubdomain.query({
        subdomain: changes.value,
      });

      if (error) {
        return dispatch(
          updateNewWorkplaceErrors({
            ...state.workplace.newWorkplace.errors,
            subdomain: error.code,
          }),
        );
      }

      if (result) {
        return dispatch(
          updateNewWorkplaceErrors({
            ...state.workplace.newWorkplace.errors,
            subdomain: undefined,
          }),
        );
      }
    }
  },
);

export const workplaceSlice = createSlice({
  name: "workplace",
  initialState,
  reducers: {
    setValidating: (state, action: PayloadAction<string>) => {
      state.workplace.isValidating = true;
      state.workplace.lastValidatedField = action.payload;
    },
    setCurrent: (
      state,
      action: PayloadAction<{
        workplaceId?: string;
        subdomain?: string;
        currentUserId?: string;
      }>,
    ) => {
      const workplace = state.workplaces.data.find(
        (w) =>
          w.id === action.payload.workplaceId ||
          w.subdomain == action.payload.subdomain,
      );
      if (!workplace) {
        state.workplace.data = null;
        state.currentMember.data = null;
        return;
      }
      state.workplace.data = workplace;

      if (action.payload.currentUserId) {
        const currentMember = workplace.members.find(
          (member) => member.clerkUserId === action.payload.currentUserId,
        );
        state.currentMember.data = currentMember ?? null;
      }
    },
    addWorkplace: (state, action: PayloadAction<Workplace>) => {
      state.workplaces.data.push(action.payload);
      state.workplace.data = action.payload;
    },
    setCurrentUploadState: (state, action: PayloadAction<boolean>) => {
      state.workplace.isUploading = action.payload;
    },
    updateField(
      state,
      action: PayloadAction<{ id: keyof Workplace; value: string }>,
    ) {
      const { id, value } = action.payload;

      // if no name is provided, set the error
      if (id == "name" && value.length === 0) {
        state.workplace.fieldErrors.name = "Name is a required field";
      } else {
        state.workplace.fieldErrors.name = undefined;
      }

      if (state.workplace.data === null) return;

      if (state.workplace.lastValidatedField == id) {
        state.workplace.isValidating = false;
      }

      state.workplace.data = {
        ...state.workplace.data,
        [id]: value,
      };
    },
    initNewWorkplace: (
      state,
      action: PayloadAction<WorkplaceState["newWorkplace"]>,
    ) => {
      state.newWorkplace = { ...state.newWorkplace, ...action.payload };
    },
    updateNewWorkplaceField: (
      state,
      action: PayloadAction<{ id: NewWorkplaceEditableFields; value: string }>,
    ) => {
      state.newWorkplace[action.payload.id] = action.payload.value;
      state.newWorkplace.errors[action.payload.id] = undefined;
    },
    updateNewWorkplaceErrors: (
      state,
      action: PayloadAction<WorkplaceState["newWorkplace"]["errors"]>,
    ) => {
      state.newWorkplace.errors = action.payload;
    },
    updateUnreadMessageCount(state, action: PayloadAction<{ count: number }>) {
      if (
        state.workplace.data &&
        state.workplace.data.unreadMessageCount !== undefined
      ) {
        state.workplace.data.unreadMessageCount += action.payload.count;
      }
    },
    updateNavigationItem: (state, action: PayloadAction<GetNavigationItem>) => {
      if (state.workplace.data) {
        state.workplace.data.navigationItems = upsertNavigationItemInTree(
          state.workplace.data.navigationItems,
          action.payload,
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getWorkplaces.pending, (state) => {
      state.workplaces.isLoading = true;
      state.workplaces.isFetched = true;
    });
    builder.addCase(getWorkplaces.fulfilled, (state, action) => {
      state.workplaces.isLoading = false;
      state.workplace.isLoading = false;

      // Only set current workplace when a subdomain is provided (not on landing page)
      // This prevents protected tRPC calls from firing on `/` where there's no subdomain context
      if (action.meta.arg.subdomain) {
        const workplace = action.payload?.records.find(
          (w) => w.subdomain === action.meta.arg.subdomain,
        );
        if (workplace) {
          state.workplace.data = workplace;
        }
      }
      // When no subdomain, just populate the list without setting a current workplace

      state.workplaces.data = action.payload?.records || [];

      /**
       * get the current member
       */
      if (state.workplace.data) {
        const currentMember = state.workplace.data.members.find(
          (m) => m.clerkUserId === action.meta.arg.currentUserId,
        );

        if (currentMember) {
          state.currentMember.data = currentMember;
        }
      }
    });
    builder.addCase(getWorkplaces.rejected, (state) => {
      state.workplaces.isLoading = false;
      state.workplace.isLoading = false;
      state.workplace.data = null;
      state.workplaces.data = [];
    });
    builder.addCase(refreshMyWorkspaceNav.fulfilled, (state, action) => {
      if (state.workplace.data && action.payload) {
        state.workplace.data.myWorkspaceNavigationItems =
          action.payload.myWorkspaceNavigationItems;
        state.workplace.data.teamNavigationItems =
          action.payload.teamNavigationItems;
        state.workplace.data.navigationItems = action.payload.navigationItems;
      }
    });
    builder.addCase(updateCurrentWorkplace.pending, (state) => {
      state.workplace.isSaving = true;
    });
    builder.addCase(updateCurrentWorkplace.fulfilled, (state, action) => {
      state.workplace = {
        data: {
          ...action.payload,
          members: state.workplace.data?.members || [],
          myWorkspaceNavigationItems:
            state.workplace.data?.myWorkspaceNavigationItems || [],
          teamNavigationItems:
            state.workplace.data?.teamNavigationItems || [],
          navigationItems: state.workplace.data?.navigationItems || [],
        },
        isUploading: false,
        isSaving: false,
        isLoading: false,
        isValidating: false,
        lastValidatedField: "",
        fieldErrors: {},
      };
    });
    builder.addCase(updateCurrentWorkplace.rejected, (state) => {
      state.workplace.data = null;
      state.workplaces.data = [];
    });
    builder.addCase(setWorkplaceArchived.pending, (state) => {
      state.workplace.isSaving = true;
    });
    builder.addCase(setWorkplaceArchived.fulfilled, (state, action) => {
      state.workplace.isSaving = false;
      const archivedAt = action.payload.archivedAt ?? null;
      if (state.workplace.data) {
        state.workplace.data = {
          ...state.workplace.data,
          archivedAt,
        };
      }
      state.workplaces.data = state.workplaces.data.map((workplace) =>
        workplace.id === action.payload.id
          ? { ...workplace, archivedAt }
          : workplace,
      );
    });
    builder.addCase(setWorkplaceArchived.rejected, (state) => {
      state.workplace.isSaving = false;
    });
    builder.addCase(updateSubdomain.pending, (state) => {
      state.newWorkplace.isLoading = true;
    });
    builder.addCase(updateSubdomain.fulfilled, (state) => {
      state.newWorkplace.isLoading = false;
    });
    builder.addCase(updateSubdomain.rejected, (state) => {
      state.newWorkplace.isLoading = false;
    });
    builder.addCase(createWorkplace.pending, (state) => {
      state.newWorkplace.isSaving = true;
    });
    builder.addCase(createWorkplace.fulfilled, (state) => {
      state.newWorkplace.isSaving = false;
    });
    builder.addCase(createWorkplace.rejected, (state) => {
      state.newWorkplace.isSaving = false;
    });
    builder.addCase(updateModel.fulfilled, (state, action) => {
      // Skip if this was a migration that requires approval
      if (
        "requiresApproval" in action.payload &&
        action.payload.requiresApproval
      ) {
        return;
      }
      const payload = action.payload as {
        navigationItem?: { page: { id: string } };
      };
      if (state.workplace.data && payload.navigationItem) {
        state.workplace.data.teamNavigationItems = upsertNavigationItemInTree(
          state.workplace.data.teamNavigationItems ?? [],
          payload.navigationItem as GetNavigationItem,
        );
        state.workplace.data.navigationItems = [
          ...(state.workplace.data.myWorkspaceNavigationItems ?? []),
          ...(state.workplace.data.teamNavigationItems ?? []),
        ];
      }
    });
    builder.addCase(createModel.fulfilled, (state, action) => {
      // Skip if this was a migration that requires approval
      if (
        "requiresApproval" in action.payload &&
        action.payload.requiresApproval
      ) {
        return;
      }
      const payload = action.payload as {
        navigationItem?: { page: { id: string } };
      };
      if (state.workplace.data && payload.navigationItem) {
        state.workplace.data.teamNavigationItems = upsertNavigationItemInTree(
          state.workplace.data.teamNavigationItems ?? [],
          payload.navigationItem as GetNavigationItem,
        );
        state.workplace.data.navigationItems = [
          ...(state.workplace.data.myWorkspaceNavigationItems ?? []),
          ...(state.workplace.data.teamNavigationItems ?? []),
        ];
      }
    });

    builder.addCase(deleteModel.fulfilled, (state, action) => {
      if (state.workplace.data) {
        const ids = (action.payload as { navigationItemsIds?: string[] })
          .navigationItemsIds;
        if (Array.isArray(ids) && ids.length > 0) {
          state.workplace.data.teamNavigationItems = filterNavigationTreeByIds(
            state.workplace.data.teamNavigationItems ?? [],
            ids,
          );
          state.workplace.data.navigationItems = [
            ...(state.workplace.data.myWorkspaceNavigationItems ?? []),
            ...(state.workplace.data.teamNavigationItems ?? []),
          ];
        }
      }
    });
  },
});

// Action creators are generated for each case reducer function
export const {
  // Workplace actions
  updateField,
  setCurrent,
  addWorkplace,
  setCurrentUploadState,
  setValidating,
  updateUnreadMessageCount,
  initNewWorkplace,
  updateNewWorkplaceField,
  updateNewWorkplaceErrors,
  updateNavigationItem,
} = workplaceSlice.actions;

export const workplaceReducer = workplaceSlice.reducer;
